import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import {
  ILiveChatAgentStatus,
  ILiveChatConversationSummary,
  ILiveChatMessage,
  normalizeAgentStatus,
  normalizeMessage,
  normalizeSummary,
} from "../models/ILiveChat";

const HUB_URL =
  process.env.REACT_APP_LIVECHAT_HUB_URL ??
  (process.env.REACT_APP_HUB_URL
    ? process.env.REACT_APP_HUB_URL.replace(/\/hubs\/order\/?$/i, "/hubs/livechat")
    : "https://service.mutfacikservices.com/hubs/livechat");

interface UseLiveChatAgentHubOptions {
  token: string | null;
  onMessage?: (message: ILiveChatMessage) => void;
  onQueued?: (conversation: ILiveChatConversationSummary) => void;
  onConversationUpdated?: (conversation: ILiveChatConversationSummary) => void;
  onAssigned?: (conversationId: string, agentId: string, agentName: string | null) => void;
  onClosed?: (conversationId: string) => void;
  onAgentPresence?: (status: ILiveChatAgentStatus) => void;
  onConnected?: () => void;
}

export function useLiveChatAgentHub(options: UseLiveChatAgentHubOptions) {
  const { token } = options;

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const joinedConversationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!token) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, { accessTokenFactory: () => token })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000, 60000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("ReceiveMessage", (raw: ILiveChatMessage) => {
      optionsRef.current.onMessage?.(normalizeMessage(raw));
    });
    connection.on("ConversationQueued", (raw: ILiveChatConversationSummary) => {
      optionsRef.current.onQueued?.(normalizeSummary(raw));
    });
    connection.on("ConversationUpdated", (raw: ILiveChatConversationSummary) => {
      optionsRef.current.onConversationUpdated?.(normalizeSummary(raw));
    });
    connection.on(
      "ConversationAssigned",
      (conversationId: string, agentId: string, agentName: string | null) => {
        optionsRef.current.onAssigned?.(conversationId, agentId, agentName);
      }
    );
    connection.on("ConversationClosed", (conversationId: string) => {
      optionsRef.current.onClosed?.(conversationId);
    });
    connection.on("AgentPresenceChanged", (raw: ILiveChatAgentStatus) => {
      optionsRef.current.onAgentPresence?.(normalizeAgentStatus(raw));
    });

    const joinDesk = () =>
      connection
        .invoke("JoinAgentDesk")
        .then(() => {
          const openId = joinedConversationRef.current;
          if (openId) return connection.invoke("JoinConversation", openId);
          return undefined;
        })
        .then(() => optionsRef.current.onConnected?.())
        .catch((err) => console.error("[LiveChat] JoinAgentDesk başarısız:", err));

    connection
      .start()
      .then(joinDesk)
      .catch((err) => console.error("[LiveChat] Bağlantı hatası:", err));

    connection.onreconnected(joinDesk);

    return () => {
      connection.off("ReceiveMessage");
      connection.off("ConversationQueued");
      connection.off("ConversationUpdated");
      connection.off("ConversationAssigned");
      connection.off("ConversationClosed");
      connection.off("AgentPresenceChanged");
      connection.stop();
      connectionRef.current = null;
      joinedConversationRef.current = null;
    };
  }, [token]);

  const joinConversation = (conversationId: string | null) => {
    const connection = connectionRef.current;
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      joinedConversationRef.current = conversationId;
      return;
    }
    const previous = joinedConversationRef.current;
    if (previous === conversationId) return;
    if (previous) connection.invoke("LeaveConversation", previous).catch(() => {});
    joinedConversationRef.current = conversationId;
    if (conversationId) {
      connection.invoke("JoinConversation", conversationId).catch((err) =>
        console.error("[LiveChat] JoinConversation başarısız:", err)
      );
    }
  };

  const notifyTyping = (conversationId: string, isTyping: boolean) => {
    const connection = connectionRef.current;
    if (connection?.state !== signalR.HubConnectionState.Connected) return;
    connection.invoke("NotifyTyping", conversationId, isTyping).catch(() => {});
  };

  return { joinConversation, notifyTyping };
}
